import type { MarketingStudioData } from '@/types/marketingStudio';
import { isSocialObservation, socialHandle } from './marketingSocialProfiles';

/** Narrows a shared director/preview snapshot; database RLS remains the access boundary. */
export function marketingLoanOfficerSocialData(
  data: MarketingStudioData,
  loUserId?: string | null,
  profileUrls?: { instagram?: string | null; tiktok?: string | null }
): MarketingStudioData {
  const person = loUserId
    ? data.participants.find((person) => person.user_id === loUserId)
    : undefined;
  const profiles =
    profileUrls ??
    (person ? { instagram: person.instagram_url, tiktok: person.tiktok_url } : undefined);
  const handle = (value: string) => value.trim().replace(/^@/, '').toLowerCase();
  const loChannels = loUserId
    ? data.loChannels.filter(
        (channel) =>
          channel.lo_user_id === loUserId &&
          (channel.platform === 'instagram' || channel.platform === 'tiktok') &&
          channel.monitoring_kind !== 'company' &&
          channel.monitoring_kind !== 'unassigned' &&
          // Channels retain old accounts after a profile change; only current links are active.
          (!profiles ||
            socialHandle(channel.platform, profiles[channel.platform]) === handle(channel.handle))
      )
    : [];
  const channels = new Map(loChannels.map((channel) => [channel.id, channel]));
  return {
    ...data,
    participants: loUserId ? data.participants.filter((person) => person.user_id === loUserId) : [],
    loChannels,
    socialHistory: data.socialHistory
      ? {
          ...data.socialHistory,
          observations: data.socialHistory.observations.filter((observation) => {
            const channel = channels.get(observation.channel_id);
            return (
              isSocialObservation(observation) &&
              channel &&
              observation.platform === channel.platform &&
              observation.provider_user_id === channel.provider_user_id
            );
          })
        }
      : undefined,
    socialReels: data.socialReels
      ? {
          ...data.socialReels,
          posts: data.socialReels.posts.filter((post) => {
            const channel = channels.get(post.channel_id);
            return (
              channel?.platform === 'instagram' &&
              post.lo_user_id === loUserId &&
              handle(post.handle) === handle(channel.handle)
            );
          })
        }
      : undefined
  };
}
